//
//  signals.ts
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: October 24, 2026
//  Author: Bob Keathley
//
//  Types and helpers for Off-Course Signals (spec §6u): the wire shapes the
//  Signals pages read, the role gate, the list query and the request bodies.
//
import { atLeast } from '../auth'

export type SignalSource = 'self_report' | 'correction' | 'rabbit_hole' | 'person'
export type SignalStatus = 'new' | 'triaged' | 'closed'
export const SIGNAL_SOURCES: SignalSource[] = ['self_report', 'correction', 'rabbit_hole', 'person']
export const SIGNAL_STATUSES: SignalStatus[] = ['new', 'triaged', 'closed']

export type SignalProblem = { what: string; correction: string; corrected_by: 'person' | 'agent' | 'gate' }
export type SignalActor = { user_id: string } | { machine_id: string; machine_user_id: string }
export type DeedRef = { notebook_id: string; seq: number }

/** One row of `GET /v1/signals` (the full `problems` are on the detail route). */
export type SignalSummary = {
  id: string
  project_id: string
  project_name: string
  agent: string
  user_id: string | null
  session_id: string
  source: SignalSource
  summary: string
  problem_count: number
  status: SignalStatus
  linked_clause: string | null
  created_at: number
  created_by: SignalActor
}

export type SignalHistoryRow = { status: SignalStatus; note: string | null; linked_clause: string | null; by: SignalActor; at: number }

/** `GET /v1/signals/{id}` and the `PATCH` response: the full signal, its history (oldest first) and project name. */
export type SignalDetail = Omit<SignalSummary, 'problem_count'> & {
  problems: SignalProblem[]
  capture_refs: string[]
  deed_refs: DeedRef[]
  triage_note: string | null
  history: SignalHistoryRow[]
}

export type SignalPage = { items: SignalSummary[]; next_cursor: string | null }

/** §6e: the `signals` list a run carries, oldest first, always present on run detail. */
export type RunSignal = { id: string; source: SignalSource; summary: string; status: SignalStatus; created_at: number }

export type SignalFilters = { project?: string; agent?: string; status?: string; source?: string }

export type SignalCreate = {
  project_id: string
  agent: string
  session_id: string
  source: 'person'
  summary: string
  problems: SignalProblem[]
  capture_refs?: string[]
  deed_refs?: DeedRef[]
}
export type SignalTriage = { status?: SignalStatus; triage_note?: string | null; linked_clause?: string | null }

/** §6u: reads any tenant user; triage and creating a signal from the console are team-admin+. */
export const canTriageSignals = (role: string | undefined) => atLeast(role, 'team-admin')

/** `/signals?…` path for the list: only non-empty filters, plus an opaque cursor. */
export function signalsListPath(f: SignalFilters, cursor?: string | null, limit = 50): string {
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(f)) if (v) q.set(k, v)
  if (cursor) q.set('cursor', cursor)
  q.set('limit', String(limit))
  return `/signals?${q.toString()}`
}

/** Human text for the §6u refusals the pages can hit. */
export function signalErrorText(e: { code: string; message: string; details?: unknown }): string {
  const d = (e.details ?? {}) as { reason?: string; path?: string; code?: string }
  if (e.code === 'assumed_read_only') return 'This assumed session is read-only.'
  if (e.code === 'forbidden') return 'Your role cannot do that.'
  if (d.code === 'no_change') return 'Nothing changed, so nothing was saved.'
  if (e.code === 'validation_error' && d.reason) return `Rejected (${d.reason}${d.path ? ` at ${d.path}` : ''}). Nothing was stored.`
  return e.message
}

/** Who made or changed it, for display. */
export function actorLabel(a: SignalActor): string {
  return 'user_id' in a ? `person ${a.user_id.slice(0, 8)}…` : `Mac ${a.machine_id.slice(0, 8)}…`
}

/** `notebook_id:seq` lines (one per line or comma-separated) to deed refs; `null` when any entry is malformed. */
export function parseDeedRefs(text: string): DeedRef[] | null {
  const out: DeedRef[] = []
  for (const raw of text.split(/[\n,]/)) {
    const t = raw.trim()
    if (!t) continue
    const i = t.lastIndexOf(':')
    const seq = Number(t.slice(i + 1))
    if (i <= 0 || !Number.isInteger(seq) || seq < 0) return null
    out.push({ notebook_id: t.slice(0, i).trim(), seq })
  }
  return out
}

export const SIGNALS_EMPTY = 'No signals yet. They appear when an agent reports a corrected problem or a person flags one.'
